import { useState } from "react";

interface ScheduleCalendarProps {
  selectedKey: string;
  onSelect: (key: string) => void;
  matchDates: Set<string>;
}

const WEEK = ["日", "一", "二", "三", "四", "五", "六"];

function keyOf(year: number, month: number, day: number): string {
  return `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

export default function ScheduleCalendar({ selectedKey, onSelect, matchDates }: ScheduleCalendarProps) {
  const initial = new Date(`${selectedKey}T00:00:00`);
  const [viewYear, setViewYear] = useState(initial.getFullYear());
  const [viewMonth, setViewMonth] = useState(initial.getMonth());

  const firstDay = new Date(viewYear, viewMonth, 1).getDay();
  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
  const cells: (number | null)[] = [
    ...Array.from({ length: firstDay }, () => null),
    ...Array.from({ length: daysInMonth }, (_, i) => i + 1),
  ];

  const shift = (delta: number) => {
    const d = new Date(viewYear, viewMonth + delta, 1);
    setViewYear(d.getFullYear());
    setViewMonth(d.getMonth());
  };

  return (
    <div className="rounded-lg border border-background-200 bg-background-50 p-4">
      <div className="flex items-center justify-between">
        <button
          type="button"
          aria-label="上一月"
          onClick={() => shift(-1)}
          className="flex w-8 h-8 items-center justify-center rounded-md text-foreground-600 transition-colors hover:bg-background-100"
        >
          <i className="ri-arrow-left-s-line" />
        </button>
        <span className="font-heading text-base font-bold text-foreground-950">
          {viewYear} 年 {viewMonth + 1} 月
        </span>
        <button
          type="button"
          aria-label="下一月"
          onClick={() => shift(1)}
          className="flex w-8 h-8 items-center justify-center rounded-md text-foreground-600 transition-colors hover:bg-background-100"
        >
          <i className="ri-arrow-right-s-line" />
        </button>
      </div>

      <div className="mt-3 grid grid-cols-7 gap-1 text-center text-[11px] text-foreground-400">
        {WEEK.map((w) => <span key={w}>{w}</span>)}
      </div>

      <div className="mt-1 grid grid-cols-7 gap-1">
        {cells.map((day, idx) => {
          if (day === null) return <span key={`empty-${idx}`} />;
          const key = keyOf(viewYear, viewMonth, day);
          const isSelected = key === selectedKey;
          const hasMatch = matchDates.has(key);
          return (
            <button
              key={key}
              type="button"
              onClick={() => onSelect(key)}
              className={`relative flex h-9 items-center justify-center rounded-md text-sm transition-colors ${
                isSelected
                  ? "bg-primary-500 font-semibold text-background-50"
                  : "text-foreground-700 hover:bg-background-100"
              }`}
            >
              {day}
              {hasMatch && !isSelected && (
                <span className="absolute bottom-1 h-1 w-1 rounded-full bg-accent-500" />
              )}
            </button>
          );
        })}
      </div>
      <p className="mt-3 flex items-center gap-1.5 text-[11px] text-foreground-400">
        <span className="h-1.5 w-1.5 rounded-full bg-accent-500" /> 表示当天有赛事安排
      </p>
    </div>
  );
}